import { useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import { DateTime } from "luxon";
import { useQuery } from "@tanstack/react-query";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import { MapContainer, TileLayer, CircleMarker, Marker, Popup, useMap } from "react-leaflet";
import { divIcon } from "leaflet";
import type { LatLngBoundsExpression, LatLngTuple } from "leaflet";
import "leaflet/dist/leaflet.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faLocationDot } from "@fortawesome/free-solid-svg-icons";

import { stoppagesAjax } from "../../ajax/StoppagesAjax";
import { OptionResponse } from "../../DTOs/Common/OptionResponse";
import GeofenceLayer from "./GeofenceLayer";
import HaulRoadLayer from "./HaulRoadLayer";
import BlastPatternFenceLayer from "./BlastPatternFenceLayer";
import DigBlockFenceLayer from "./DigBlockFenceLayer";
import DrillBlastOverlayLayer from "./DrillBlastOverlayLayer";
import MapFeatureLayer from "./MapFeatureLayer";
import { LayerStack, StackedGroup } from "./LayerStack";
import { LAYER_RANK } from "./layerStackOrder";
import { BLAST_PATTERN_STATUS_COLOURS, BLAST_PATTERN_STATUSES } from "./mapStyles";
import { fenceCorners, useGeofences } from "./useGeofences";
import { useMachinePositions } from "./useMachinePositions";
import { useMapFeatures } from "./useMapFeatures";
import { useMapZoom } from "./useMapZoom";
import { Basemap, readStoredBasemap, resolveBasemap, storeBasemap } from "./basemapPreference";
import { StoppageTypeDto } from "../../DTOs/Stoppage/StoppageTypeDto";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { DashboardCardEmptyState } from "../Dashboard/DashboardCardEmptyState";
import {
  equipmentLabel,
  equipmentMarkerHtml,
  isEquipmentType,
  useEquipmentSprite,
  OFFLINE_OPACITY,
  STATUS_COLOURS,
} from "./EquipmentMarker";

const MAX_MARKER_SIZE = 40;
const MIN_MARKER_SIZE = 20;
const FULL_SIZE_MARKER_ZOOM = 17;
const MARKER_SIZE_STEP_PER_ZOOM = 6;

function markerSize(zoom: number | null): number {
  if (zoom == null) return MAX_MARKER_SIZE;
  return Math.max(MIN_MARKER_SIZE, MAX_MARKER_SIZE - Math.max(0, FULL_SIZE_MARKER_ZOOM - zoom) * MARKER_SIZE_STEP_PER_ZOOM);
}

const MapZoomReporter: React.FC<{ onZoom: (zoom: number) => void }> = ({ onZoom }) => {
  const zoom = useMapZoom();
  useEffect(() => onZoom(zoom), [zoom, onZoom]);
  return null;
};

const MAP_MAX_ZOOM = 22;
const OSM_MAX_NATIVE_ZOOM = 19;
const MAP_ZOOM_SNAP = 0;
const OFFLINE_RECHECK_MS = 15000;
const OFFLINE_Z_OFFSET = -1000;

function formatSilence(seconds: number): string {
  if (seconds < 60) return `${Math.max(0, Math.round(seconds))} sec`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h`;
  return `${Math.floor(seconds / 86400)} d`;
}

const MOVING_WHILE_DOWN_KPH = 5;

const WORKING_STATUS = "up";
const OFFLINE_STATUS = "off";
const stoppageStatus = (stoppageTypeId: number) => `stoppage-${stoppageTypeId}`;

const STOPPAGE_TYPE_ABBREVIATIONS: Record<number, string> = {
  1: "SB",
  2: "PM",
  3: "OD",
  4: "NOD",
  5: "MD",
  6: "UC",
};

function abbreviateStoppageType(typeId: number | null, name: string | null): string {
  if (typeId != null && STOPPAGE_TYPE_ABBREVIATIONS[typeId])
    return STOPPAGE_TYPE_ABBREVIATIONS[typeId];

  if (!name)
    return "DOWN";

  const words = name.replace(/\(.*?\)/g, " ").trim().split(/\s+/).filter(Boolean);
  return words.length > 1
    ? words.map(w => w[0]).join("").toUpperCase().slice(0, 4)
    : name.slice(0, 2).toUpperCase();
}

const MapResizeWatcher: React.FC = () => {
  const map = useMap();

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => map.invalidateSize());
    observer.observe(map.getContainer());
    return () => observer.disconnect();
  }, [map]);

  return null;
};

type MachinePositionsMapViewProps = {
  title?: React.ReactNode;
  sitePlanOption?: boolean;
};

export const MachinePositionsMapView: React.FC<MachinePositionsMapViewProps> = ({ title, sitePlanOption = true }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { positions, isLoading, loadFailed } = useMachinePositions();

  const spriteStatus = useEquipmentSprite();

  const { data: stoppageTypeOptions = new OptionResponse<StoppageTypeDto>([]) } = useQuery({
    queryKey: ["references", "stoppageTypes"],
    queryFn: async () => {
      const res = await stoppagesAjax.getStoppageTypes();
      if (ErrorResponse.isErrorResponse(res)) return new OptionResponse<StoppageTypeDto>([]);
      return res;
    },
    staleTime: 5 * 60 * 1000,
  });

  const stoppageTypes = useMemo(
    () => stoppageTypeOptions.groups
      .map(g => ({
        stoppageTypeId: g.key,
        stoppageType: g.label,
        colourHex: g.fullObject?.colourHex ?? null,
      }))
      .sort((a, b) => a.stoppageTypeId - b.stoppageTypeId),
    [stoppageTypeOptions]);

  useEffect(() => {
    if (spriteStatus !== "failed") return;

    enqueueSnackbar("Equipment icons could not be loaded - showing plain position markers.",
      { variant: "warning", autoHideDuration: 6000 });
  }, [spriteStatus, enqueueSnackbar]);

  const { fences, isLoading: fencesLoading } = useGeofences();

  const initialBounds = useMemo<LatLngBoundsExpression | null>(() => {
    const machinePoints = positions.map(({ plant }) => [plant.latitude, plant.longitude] as LatLngTuple);
    const points = [...machinePoints, ...fenceCorners(fences)];

    return points.length === 0 ? null : points;
  }, [positions, fences]);

  const { features: mapFeatures, isSettled: mapFeaturesSettled } = useMapFeatures();
  const [storedBasemap, setStoredBasemap] = useState<Basemap | null>(readStoredBasemap);
  const basemap = resolveBasemap(sitePlanOption ? storedBasemap : null, mapFeaturesSettled, mapFeatures.length);

  const chooseBasemap = (sitePlan: boolean) => {
    const next: Basemap = sitePlan ? "site" : "osm";
    storeBasemap(next);
    setStoredBasemap(next);
  };

  const [showHaulRoads, setShowHaulRoads] = useState(true);
  const [showGeofences, setShowGeofences] = useState(true);
  const [showBlastPatterns, setShowBlastPatterns] = useState(true);
  const [showDigBlocks, setShowDigBlocks] = useState(true);
  const [showBlasting, setShowBlasting] = useState(true);

  const [offlineCheckedAt, setOfflineCheckedAt] = useState(() => DateTime.utc());
  const [mapZoom, setMapZoom] = useState<number | null>(null);
  const iconSize = markerSize(mapZoom);

  const offlineAfterSeconds = positions[0]?.plant.offlineAfterSeconds ?? 0;

  useEffect(() => {
    const timer = setInterval(() => setOfflineCheckedAt(DateTime.utc()), OFFLINE_RECHECK_MS);
    return () => clearInterval(timer);
  }, []);

  const rendered = useMemo(() => positions.map(({ plant, heading, headingKnown }) => {
    const silentSeconds = offlineCheckedAt.diff(plant.timestampUtc, "seconds").seconds;
    const isOffline = plant.offlineAfterSeconds > 0 && silentSeconds > plant.offlineAfterSeconds;

    const statusColour = plant.isStopped
      ? plant.stoppageColourHex ?? STATUS_COLOURS.unclassified
      : isOffline
        ? STATUS_COLOURS.offline
        : STATUS_COLOURS.working;

    const isMovingWhileDown = plant.isStopped
      && !isOffline
      && plant.speedKph != null
      && plant.speedKph > MOVING_WHILE_DOWN_KPH;

    return {
      plant,
      heading,
      isOffline,
      statusColour,
      silentSeconds,
      isMovingWhileDown,
      showWedge: !plant.isStopped && headingKnown && !isOffline,
    };
  }), [positions, offlineCheckedAt]);

  const [hiddenStatuses, setHiddenStatuses] = useState<ReadonlySet<string>>(() => new Set());

  const toggleStatus = (key: string) => {
    setHiddenStatuses(prev => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  };

  const shown = useMemo(
    () => rendered.filter(({ plant, isOffline }) => {
      if (isOffline && hiddenStatuses.has(OFFLINE_STATUS)) return false;
      if (plant.isStopped) return plant.stoppageTypeId == null || !hiddenStatuses.has(stoppageStatus(plant.stoppageTypeId));
      return isOffline || !hiddenStatuses.has(WORKING_STATUS);
    }),
    [rendered, hiddenStatuses]);

  const legend = useMemo(() => {
    const entries: { key: string; code: string; label: string; colour: string; faded: boolean }[] = [
      { key: WORKING_STATUS, code: "Up", label: "Working", colour: STATUS_COLOURS.working, faded: false },
    ];

    for (const type of stoppageTypes) {
      entries.push({
        key: stoppageStatus(type.stoppageTypeId),
        code: abbreviateStoppageType(type.stoppageTypeId, type.stoppageType),
        label: type.stoppageType,
        colour: type.colourHex ?? STATUS_COLOURS.unclassified,
        faded: false,
      });
    }

    entries.push({
      key: OFFLINE_STATUS,
      code: "Off",
      label: offlineAfterSeconds > 0
        ? `Offline - nothing heard for over ${formatSilence(offlineAfterSeconds)}, shown at its last known position`
        : "Offline - shown at its last known position",
      colour: STATUS_COLOURS.offline,
      faded: true,
    });

    return entries;
  }, [stoppageTypes, offlineAfterSeconds]);

  const emptyMessage = loadFailed
    ? "Machine positions could not be loaded."
    : "No plant has reported a position yet.";

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 shrink-0">
        {title}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-theme-text-muted">
          {sitePlanOption && (
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={basemap === "site"}
                onChange={e => chooseBasemap(e.target.checked)}
                className="w-4 h-4 border-theme-border rounded-sm"
              />
              Site plan
            </label>
          )}
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={showHaulRoads}
              onChange={e => setShowHaulRoads(e.target.checked)}
              className="w-4 h-4 border-theme-border rounded-sm"
            />
            Haul roads
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={showGeofences}
              onChange={e => setShowGeofences(e.target.checked)}
              className="w-4 h-4 border-theme-border rounded-sm"
            />
            Geofences
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={showBlastPatterns}
              onChange={e => setShowBlastPatterns(e.target.checked)}
              className="w-4 h-4 border-theme-border rounded-sm"
            />
            Blast patterns
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={showDigBlocks}
              onChange={e => setShowDigBlocks(e.target.checked)}
              className="w-4 h-4 border-theme-border rounded-sm"
            />
            Dig blocks
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={showBlasting}
              onChange={e => setShowBlasting(e.target.checked)}
              className="w-4 h-4 border-theme-border rounded-sm"
            />
            Misfires, exclusion zones and monitors
          </label>
          {showBlastPatterns && BLAST_PATTERN_STATUSES.map(status => (
            <span key={status} className="flex items-center gap-1 text-xs text-theme-text-muted">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: BLAST_PATTERN_STATUS_COLOURS[status] }} />
              {status}
            </span>
          ))}
          {legend.map(entry => {
            const isShown = !hiddenStatuses.has(entry.key);

            return (
              <Tooltip key={entry.key} title={entry.label} arrow placement="top">
                <Chip
                  label={entry.code}
                  size="small"
                  aria-label={entry.label}
                  aria-pressed={isShown}
                  onClick={() => toggleStatus(entry.key)}
                  className={isShown
                    ? "!bg-theme-primary !text-theme-primary-contrast !border !border-theme-border"
                    : "!bg-theme-surface !text-theme-text-muted !border !border-theme-table-border"}
                  icon={(
                    <span
                      className="inline-block w-3 h-3 rounded-full border border-white"
                      style={{
                        backgroundColor: entry.colour,
                        opacity: entry.faded || !isShown ? OFFLINE_OPACITY : 1,
                        boxShadow: "0 0 0 1px rgba(0,0,0,.45)",
                      }}
                    />
                  )}
                />
              </Tooltip>
            );
          })}
        </div>
      </div>

      <div className="isolate flex-1 min-h-0 rounded-lg border border-theme-border overflow-hidden relative">
        {(isLoading || fencesLoading) && (
          <div className="absolute inset-0 flex items-center justify-center bg-theme-surface z-[1000]">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-theme-text-muted" />
          </div>
        )}

        {!isLoading && !fencesLoading && !initialBounds && (
          <DashboardCardEmptyState
            icon={faLocationDot}
            title="No machine positions"
            subtitle={emptyMessage}
          />
        )}

        {!isLoading && !fencesLoading && initialBounds && positions.length === 0 && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1000] rounded-sm border border-theme-border bg-theme-surface px-3 py-1 text-sm text-theme-text-muted">
            {emptyMessage}
          </div>
        )}

        {!isLoading && !fencesLoading && initialBounds && (
          <MapContainer preferCanvas maxZoom={MAP_MAX_ZOOM} zoomSnap={MAP_ZOOM_SNAP} bounds={initialBounds} boundsOptions={{ padding: [40, 40] }} style={{ height: "100%", width: "100%" }}>
            {basemap === "osm" && (
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxZoom={MAP_MAX_ZOOM}
                maxNativeZoom={OSM_MAX_NATIVE_ZOOM}
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
            )}
            <MapZoomReporter onZoom={setMapZoom} />
            <MapResizeWatcher />
            <LayerStack>
              <MapFeatureLayer sitePlan={basemap === "site"} />
              <GeofenceLayer visible={showGeofences} />
              <HaulRoadLayer visible={showHaulRoads} />
              <BlastPatternFenceLayer visible={showBlastPatterns} />
              <DigBlockFenceLayer visible={showDigBlocks} />
              <DrillBlastOverlayLayer visible={showBlasting} />

              <StackedGroup rank={LAYER_RANK.machines}>
                {shown.map(({ plant, heading, isOffline, statusColour, showWedge, silentSeconds, isMovingWhileDown }) => {
                  const popup = (
                    <Popup>
                      <div className="text-sm text-theme-text">
                        <div className="font-semibold">{plant.plantRef}</div>
                        {isEquipmentType(plant.equipmentType) && (
                          <div className="text-theme-text-muted">{equipmentLabel(plant.equipmentType)}</div>
                        )}
                        <div>
                          {plant.isStopped ? plant.stoppageType ?? "Stopped" : "Moving"}
                          {plant.speedKph != null ? ` - ${plant.speedKph.toFixed(1)} km/h` : ""}
                          {showWedge ? ` - heading ${Math.round(((heading % 360) + 360) % 360).toString().padStart(3, "0")}` : ""}
                        </div>
                        <div>{plant.locationName ?? "Unknown location"}</div>
                        {isOffline && (
                          <div className="font-semibold">
                            Offline - no report for {formatSilence(silentSeconds)}
                          </div>
                        )}
                        {isMovingWhileDown && (
                          <div className="font-semibold text-theme-warning">
                            Moving while marked down - the stoppage may need closing
                          </div>
                        )}
                        <div className="text-xs text-theme-text-muted">{plant.timestampUtc.toFormat("dd LLL HH:mm:ss")}</div>
                      </div>
                    </Popup>
                  );

                  if (spriteStatus !== "ready" || !isEquipmentType(plant.equipmentType)) {
                    return (
                      <CircleMarker
                        key={plant.plantId}
                        center={[plant.latitude, plant.longitude]}
                        radius={8}
                        pathOptions={{
                          weight: 2,
                          color: statusColour,
                          fillColor: statusColour,
                          fillOpacity: isOffline ? OFFLINE_OPACITY : 0.85,
                          opacity: isOffline ? OFFLINE_OPACITY : 1,
                        }}
                      >
                        {popup}
                      </CircleMarker>
                    );
                  }

                  return (
                    <Marker
                      key={plant.plantId}
                      position={[plant.latitude, plant.longitude]}
                      zIndexOffset={isOffline ? OFFLINE_Z_OFFSET : 0}
                      icon={divIcon({
                        className: "eq-marker",
                        html: equipmentMarkerHtml({
                          type: plant.equipmentType,
                          heading,
                          size: iconSize,
                          moving: showWedge,
                          statusColour,
                          opacity: isOffline ? OFFLINE_OPACITY : 1,
                        }),
                        iconSize: [iconSize, iconSize],
                        iconAnchor: [iconSize / 2, iconSize / 2],
                      })}
                    >
                      {popup}
                    </Marker>
                  );
                })}
              </StackedGroup>
            </LayerStack>
          </MapContainer>
        )}
      </div>
    </>
  );
};
